<div class="colleague-settings-container">
    <div class="settings-header">
        <div class="settings-header">
            <h2>TARIFFS & PAYMENT SETTINGS</h2>
            <p class="hint">
                Configure your billing defaults in one place. Configure your Medical Scheme price groups, apply tariff
                multipliers, and set a default rate for rapid coding. Keep VAT numbers, banking details, and billing
                contacts up to date so every quotation and invoice is generated quickly, accurately, and in line with
                your contracts or preferred rates.
            </p>
        </div>
    </div>
    <mat-accordion multi="false">
        <mat-expansion-panel [expanded]="true">
            <mat-expansion-panel-header>
                <mat-panel-title>
                    MY TARIFF RATES
                    <mat-icon class="expansion-header-icon" (click)="openAddTarrifRatesDialog($event)"
                        matTooltip="Add your tariff rates" color="primary">add</mat-icon>
                </mat-panel-title>
            </mat-expansion-panel-header>

            <mat-form-field appearance="outline" class="full-width">
                <mat-label>Tariff Rates</mat-label>
                <mat-chip-grid #chipGrid aria-label="Enter tariff rates">
                    <mat-chip-row *ngFor="let tariff of tariffRates; trackBy: trackByTariffId"
                        appTouchEdit (dblclick)="editTariff(tariff)" [matTooltip]="getTariffTooltip(tariff)">
                        {{ tariff.name }}
                        <button matChipRemove (click)="removeTariff(tariff)">
                            <mat-icon>cancel</mat-icon>
                        </button>
                    </mat-chip-row>
                </mat-chip-grid>
                <input [matChipInputFor]="chipGrid" disabled />
                <mat-hint>Double click or tap and hold a tariff rate to edit its information</mat-hint>
            </mat-form-field>
        </mat-expansion-panel>


        <mat-expansion-panel>
            <mat-expansion-panel-header>
                <mat-panel-title>
                    TARIFF PRICE OVERRIDES
                </mat-panel-title>
            </mat-expansion-panel-header>
            <p class="hint">Set custom pricing for specific procedure codes that differ from standard tariff rates. This
                allows you to override the default pricing for individual procedures, such as consultations or
                specialized treatments, ensuring accurate billing for each specific case.</p>
            <app-procedure-code-selector [initialCodes]="group.tariffOverrides"
                (codesChanged)="onTariffOverrideCodesChanged($event)" [collectRandValue]="true"
                [handleEditInternal]="true"></app-procedure-code-selector>
        </mat-expansion-panel>

        <mat-expansion-panel>
            <mat-expansion-panel-header>
                <mat-panel-title>
                    CONSUMABLE (MEDS/MATERIALS) PRICE OVERRIDES
                </mat-panel-title>
            </mat-expansion-panel-header>
            <p class="hint">Set fixed prices for specific medicines and materials. These rates will be used when adding
                those items to quotes and invoices, and can be overridden per line if needed.</p>
            <app-consumable-override-selector [initialOverrides]="group.consumableOverrides || []"
                (overridesChanged)="onConsumableOverridesChanged($event)"></app-consumable-override-selector>
        </mat-expansion-panel>

        <mat-expansion-panel>
            <mat-expansion-panel-header>
                <mat-panel-title>
                    BUSINESS DETAILS
                    <mat-icon class="expansion-header-icon" (click)="openAddBusinessDetailsDialog($event)"
                        matTooltip="Add business details" color="primary">edit</mat-icon>
                </mat-panel-title>
            </mat-expansion-panel-header>
            <ng-container *ngIf="group; else loading">
                <div class="section-container">
                    <h3>Business Information</h3>
                    <p><strong>Practice Name:</strong> {{ group.companyName || 'Not configured' }}</p>
                    <p><strong>Registration Number:</strong> {{ group.registrationNumber || 'Not configured' }}</p>
                    <p><strong>VAT Number:</strong> {{ group.vatNumber || 'Not configured' }}</p>
                    <p><strong>Address:</strong> {{ group.address || 'Not configured' }}</p>
                </div>

                <div class="section-container" *ngIf="group.bankingDetails">
                    <h3>Banking Details</h3>
                    <p><strong>Account Name:</strong> {{ group.bankingDetails.accountName || 'Not configured' }}</p>
                    <p><strong>Bank Name:</strong> {{ group.bankingDetails.bankName || 'Not configured' }}</p>
                    <p><strong>Account Type:</strong> {{ group.bankingDetails.accountType || 'Not configured' }}</p>
                    <p><strong>Account Number:</strong> {{ group.bankingDetails.accountNumber || 'Not configured' }}</p>
                    <p><strong>Branch Name:</strong> {{ group.bankingDetails.branchName || 'Not configured' }}</p>
                    <p><strong>Branch Code:</strong> {{ group.bankingDetails.branchCode || 'Not configured' }}</p>
                </div>
                <div class="section-container" *ngIf="!group.bankingDetails">
                    <h3>Banking Details</h3>
                    <p class="not-configured">No banking details have been configured yet.</p>
                </div>

                <div class="section-container">
                    <h3>Accounts Contact Information</h3>
                    <p><strong>Contact Number:</strong> {{ group.accountsPhone || 'Not configured' }}</p>
                    <p><strong>Email:</strong> {{ group.accountsEmail || 'Not configured' }}</p>
                </div>
            </ng-container>
        </mat-expansion-panel>

        <mat-expansion-panel>
            <mat-expansion-panel-header>
                <mat-panel-title>
                    PAYMENT GATEWAY INTEGRATION
                    <mat-icon class="expansion-header-icon" (click)="openYocoIntegrationDialog($event)"
                        matTooltip="Add Yoco integration" color="primary">edit</mat-icon>
                </mat-panel-title>
            </mat-expansion-panel-header>
            <ng-container *ngIf="group; else loading">
                <div class="section-container">
                    <h3>YOCO Integration</h3>
                    <p><strong>YOCO Public Key:</strong> {{ group.yocoPublicKey ? group.yocoPublicKey : 'Not configured'
                        }}</p>
                    <p><strong>YOCO Secret Key:</strong> {{ group.yocoSecretKey ? group.yocoSecretKey : 'Not configured'
                        }}</p>
                    <p><strong>Webhook Status:</strong> {{ group.yocoWebhookId ? 'Registered' : 'Not registered' }}</p>
                    <p class="hint" *ngIf="!group.yocoPublicKey || !group.yocoSecretKey">
                        Configure your YOCO integration to enable QR code payments from the invoices generated by
                        MedicAI. You can find
                        your keys in the YOCO dashboard. <a
                            href="https://developer.yoco.com/online/resources/integration-keys/#2-fetch-your-keys-from-the-yoco-business-portal"
                            target="_blank">Click here</a> to learn more.
                    </p>
                    <p class="hint" *ngIf="group.yocoPublicKey && group.yocoSecretKey && !group.yocoWebhookId">
                        Webhook will be automatically registered when you save your Yoco credentials to enable automatic
                        payment processing.
                    </p>
                </div>
            </ng-container>
        </mat-expansion-panel>
    </mat-accordion>
    <div class="mobile-spacer"></div>
</div>

<ng-template #addTarrifRatesDialog>
    <h1 mat-dialog-title>Tariff Rates</h1>
    <div mat-dialog-content>
        <p class="hint">
            Configure your tariff rates for quotations and invoices. Network rates (MPA) are used when you are part of a
            medical scheme network
            and have contracted rates for that specific scheme. Non-network rates (MSR) are used for standard billing.
            The modifier is applied as a
            percentage to the base rate (e.g., 100 means no change, 120 means 20% increase).
        </p>
        <div class="p-t-20 field-column">
            <div class="toggle-container">
                <mat-slide-toggle color="primary" [(ngModel)]="editingTariffs.isNetworkDoctor"
                    (change)="onNetworkToggle($event)">
                    Create Contracted Rate
                </mat-slide-toggle>
            </div>
            <br>
            <mat-form-field appearance="outline" class="full-width">
                <mat-label>Name</mat-label>
                <input matInput [(ngModel)]="editingTariffs.name" placeholder="Name" type="text" required>
            </mat-form-field>
            <mat-form-field appearance="outline" class="full-width">
                <mat-label>Select Base Rate</mat-label>
                <input type="text" matInput [formControl]="doctorBaseRateControl" [matAutocomplete]="autoBaseRate"
                    placeholder="Type to search base rates…" />
                <mat-autocomplete #autoBaseRate="matAutocomplete" [displayWith]="displayBaseRate"
                    (optionSelected)="onDoctorBaseRateSelected($event.option.value)">
                    <mat-option *ngFor="let pg of filteredDoctorBaseRates | async" [value]="pg">
                        {{ pg.description }} - {{ pg.code }}
                    </mat-option>
                </mat-autocomplete>
            </mat-form-field>
            <!-- <mat-form-field appearance="outline" class="full-width">
                <mat-label>Default Medical Scheme</mat-label>
                <input type="text" matInput [formControl]="patientSchemeControl"
                    [matAutocomplete]="autoPatientScheme"
                    placeholder="Type at least 1 character" (blur)="onPatientSchemeBlur()">
                <mat-autocomplete #autoPatientScheme="matAutocomplete" [displayWith]="displayScheme"
                    (optionSelected)="onPatientSchemeSelected($event.option.value)">
                    <mat-option *ngFor="let scheme of filteredPatientSchemes | async" [value]="scheme">
                        {{ scheme.name }}
                    </mat-option>
                </mat-autocomplete>
            </mat-form-field>

            <mat-form-field appearance="outline" class="full-width">
                <mat-label>Default Plan Option</mat-label>
                <mat-select [(ngModel)]="editingTariffs.planOptions">
                    <mat-option *ngFor="let p of planOptions" [value]="p.code">
                        {{p.planCode}} – {{p.option}}
                    </mat-option>
                </mat-select>
            </mat-form-field> -->
            <mat-form-field appearance="outline" class="full-width">
                <mat-label>Modifier</mat-label>
                <input matInput [(ngModel)]="editingTariffs.modifier" placeholder="Modifier" type="number" min="0"
                    max="1000" required>
                <span matSuffix class="p-r-5">%</span>
                <mat-hint>100 means no change, 120 means 20% increase</mat-hint>
            </mat-form-field>

            <div class="toggle-container">
                <mat-slide-toggle color="primary" [(ngModel)]="editingTariffs.isDefaultRate"
                    (change)="onDefaultToggle($event)">
                    Default Rate
                </mat-slide-toggle>
            </div>
        </div>
    </div>
    <div mat-dialog-actions class="dialog-actions">
        <button mat-raised-button (click)="saveTariffRates()" color="primary">Save</button>
    </div>
</ng-template>

<ng-template #addBusinessDetailsDialog>
    <h1 mat-dialog-title>Business Details</h1>
    <div mat-dialog-content>
        <div class="p-t-20 field-column">
            <div class="form-section">
                <h3>Business Details</h3>
                <mat-form-field appearance="outline" class="full-width">
                    <mat-label>Practice Name</mat-label>
                    <input matInput [(ngModel)]="editingBusinessDetails.companyName" placeholder="Company Name"
                        type="text" required>
                </mat-form-field>
                <mat-form-field appearance="outline" class="full-width">
                    <mat-label>Registration Number</mat-label>
                    <input matInput [(ngModel)]="editingBusinessDetails.registrationNumber"
                        placeholder="Registration Number" type="text" required>
                </mat-form-field>
                <mat-form-field appearance="outline" class="full-width">
                    <mat-label>VAT Number</mat-label>
                    <input matInput [(ngModel)]="editingBusinessDetails.vatNumber" placeholder="VAT Number" type="text">
                </mat-form-field>
                <mat-form-field appearance="outline">
                    <mat-label>Address</mat-label>
                    <textarea matInput cdkTextareaAutosize="true"
                        [(ngModel)]="editingBusinessDetails.address"></textarea>
                </mat-form-field>
            </div>

            <div class="form-section">
                <h3>Banking Details</h3>
                <mat-form-field appearance="outline" class="full-width">
                    <mat-label>Account Name</mat-label>
                    <input matInput [(ngModel)]="editingBusinessDetails.bankingDetails.accountName"
                        placeholder="Account Name" type="text" required>
                </mat-form-field>
                <mat-form-field appearance="outline" class="full-width">
                    <mat-label>Bank Name</mat-label>
                    <input matInput [(ngModel)]="editingBusinessDetails.bankingDetails.bankName" placeholder="Bank Name"
                        type="text" required>
                </mat-form-field>
                <mat-form-field appearance="outline" class="full-width">
                    <mat-label>Account Type</mat-label>
                    <input matInput [(ngModel)]="editingBusinessDetails.bankingDetails.accountType"
                        placeholder="Account Type" type="text" required>
                </mat-form-field>
                <mat-form-field appearance="outline" class="full-width">
                    <mat-label>Account Number</mat-label>
                    <input matInput [(ngModel)]="editingBusinessDetails.bankingDetails.accountNumber"
                        placeholder="Account Number" type="text" required>
                </mat-form-field>
                <mat-form-field appearance="outline" class="full-width">
                    <mat-label>Branch Name</mat-label>
                    <input matInput [(ngModel)]="editingBusinessDetails.bankingDetails.branchName"
                        placeholder="Branch Name" type="text" required>
                </mat-form-field>
                <mat-form-field appearance="outline" class="full-width">
                    <mat-label>Branch Code</mat-label>
                    <input matInput [(ngModel)]="editingBusinessDetails.bankingDetails.branchCode"
                        placeholder="Branch Code" type="text" required>
                </mat-form-field>
            </div>

            <div class="form-section">
                <h3>Accounts Contact Information</h3>
                <app-intl-phone-input [preferredCountries]="['za']"
                    [providedValue]="editingBusinessDetails.accountsPhone" placeholder="Accounts Contact Number"
                    (valueChange)="editingBusinessDetails.accountsPhone = $event" name="contactNumber"
                    classList="full-width">
                </app-intl-phone-input>

                <mat-form-field appearance="outline" class="full-width">
                    <mat-label>Accounts Email</mat-label>
                    <input matInput type="email" [(ngModel)]="editingBusinessDetails.accountsEmail" required>
                </mat-form-field>
            </div>
        </div>
    </div>
    <div mat-dialog-actions class="dialog-actions">
        <button mat-raised-button color="primary" (click)="saveBusinessDetails()">Save</button>
    </div>
</ng-template>

<ng-template #yocoIntegrationDialog>
    <h1 mat-dialog-title>YOCO Details</h1>
    <div mat-dialog-content>
        <div class="p-t-20 field-column">
            <mat-form-field appearance="outline" class="full-width">
                <mat-label>YOCO Public Key</mat-label>
                <input matInput [(ngModel)]="editingYocoDetails.yocoPublicKey" placeholder="YOCO Public Key"
                    [type]="showPublicKey ? 'text' : 'password'" required>
                <button mat-icon-button matSuffix (click)="showPublicKey = !showPublicKey" type="button">
                    <mat-icon>{{showPublicKey ? 'visibility_off' : 'visibility'}}</mat-icon>
                </button>
            </mat-form-field>
            <mat-form-field appearance="outline" class="full-width">
                <mat-label>YOCO Secret Key</mat-label>
                <input matInput [(ngModel)]="editingYocoDetails.yocoSecretKey" placeholder="YOCO Secret Key"
                    [type]="showSecretKey ? 'text' : 'password'" required>
                <button mat-icon-button matSuffix (click)="showSecretKey = !showSecretKey" type="button">
                    <mat-icon>{{showSecretKey ? 'visibility_off' : 'visibility'}}</mat-icon>
                </button>
            </mat-form-field>
        </div>
    </div>
    <div mat-dialog-actions class="dialog-actions">
        <!-- (click)="saveYocoDetails()" -->
        <button mat-raised-button color="primary" (click)="saveYocoDetails()">Save</button>
    </div>
</ng-template>

<!-- <ng-template #medicalAidDialog>
    <h1 mat-dialog-title>Medical Aid Scheme</h1>
    <div mat-dialog-content>
        <div class="p-t-20 field-column">
            <mat-form-field appearance="outline" class="full-width">
                <mat-label>Scheme</mat-label>
                <input type="text" matInput [formControl]="patientSchemeControl" [matAutocomplete]="autoPatientScheme"
                placeholder="Type at least 1 character" (blur)="onPatientSchemeBlur()">
                <mat-autocomplete #autoPatientScheme="matAutocomplete" [displayWith]="displayScheme"
                    (optionSelected)="onPatientSchemeSelected($event.option.value)">
                    <mat-option *ngFor="let scheme of filteredPatientSchemes | async" [value]="scheme">
                        {{ scheme.name }}
                    </mat-option>
                </mat-autocomplete>
              </mat-form-field>
          
              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Plan Option</mat-label>
                <mat-select [(ngModel)]="planOptions">
                  <mat-option *ngFor="let p of planOptions" [value]="p.code">
                    {{p.planCode}} – {{p.option}}
                  </mat-option>
                </mat-select>
              </mat-form-field>
        </div>
    </div>
    <div mat-dialog-actions class="dialog-actions">
        <button mat-raised-button color="primary" (click)="saveMedicalAidDetails()">Save</button>
    </div>
</ng-template> -->

<ng-template #loading>
    <div class="loader">
        <mat-spinner></mat-spinner>
        <p>Loading details...</p>
    </div>
</ng-template>